import * as msal from "@azure/msal-browser";

import { loginRequest } from "./loginRequest";
import { msalInstance } from "./msalInstance";
import { redirectToLogin } from "./redirectToLogin";

/**
 * Acquires an access token for the current user using MSAL.
 * Uses acquireTokenSilent to return a cached token if still valid, or automatically
 * refreshes it using the refresh token when it has expired — without user interaction.
 * Redirects to the login page only if the session has fully expired and interaction is required.
 * Returns null when no token can be obtained.
 * @see https://learn.microsoft.com/en-us/entra/msal/javascript/browser/token-lifetimes
 */
export async function getAccessToken(): Promise<string | null> {
    const account =
        msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

    if (!account) {
        await redirectToLogin();
        return null;
    }

    try {
        const result = await msalInstance.acquireTokenSilent({
            account,
            scopes: loginRequest.scopes
        });
        return result.accessToken;
    } catch (error) {
        if (error instanceof msal.InteractionRequiredAuthError) {
            await redirectToLogin();
        } else {
            // Non-interaction errors (e.g., network failures) are logged but not surfaced
            // to the caller. The null return will cause callers to skip setting the
            // Authorization header, resulting in a 401 handled by the response interceptor.
            // eslint-disable-next-line no-console
            console.warn("getAccessToken: acquireTokenSilent failed.", error);
        }
        return null;
    }
}
